/**
 * NotificationPreferencesEditor (Module C) — the current user's per-org email
 * preference. In-app (the bell) is always on; this controls whether the user
 * receives org notifications by email and from which level. Telegram/WhatsApp
 * are org-level channels (fixed destinations), not per-user.
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { notificationsApi } from '../../services/api';
import { useOrganizationStore } from '../../stores/organization';
import { LoadingSpinner } from '../common';

type Level = 'info' | 'warning' | 'critical';

export default function NotificationPreferencesEditor() {
  const selectedOrg = useOrganizationStore((s) => s.selectedOrg);
  const orgId = selectedOrg?._id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [minLevel, setMinLevel] = useState<Level>('info');

  useEffect(() => {
    if (!orgId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    notificationsApi.getPreferences()
      .then((r) => {
        if (cancelled) return;
        const e = r.data?.data?.email || {};
        setEnabled(e.enabled ?? true);
        setMinLevel((e.minLevel as Level) || 'info');
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [orgId]);

  const save = async (next: { enabled: boolean; minLevel: Level }) => {
    setSaving(true);
    try {
      await notificationsApi.savePreferences({ email: next });
      toast.success('Preferencias guardadas');
    } catch {
      toast.error('Error al guardar preferencias');
    } finally {
      setSaving(false);
    }
  };

  if (!orgId) {
    return <p className="text-sm text-gray-500">Selecciona una organización para configurar tus preferencias.</p>;
  }
  if (loading) {
    return <div className="flex items-center justify-center py-6 text-gray-400"><LoadingSpinner size="sm" /></div>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Preferencias para <strong>{selectedOrg?.name}</strong>. La campana (in-app) siempre está activa;
        aquí controlas el <strong>email</strong>.
      </p>

      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
        <div>
          <p className="font-medium text-gray-900">Recibir notificaciones por email</p>
          <p className="text-sm text-gray-500">Si lo desactivas, no te llegarán emails de esta organización.</p>
        </div>
        <button
          type="button"
          onClick={() => { const v = !enabled; setEnabled(v); void save({ enabled: v, minLevel }); }}
          disabled={saving}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-vida-600' : 'bg-gray-200'} ${saving ? 'opacity-50' : ''}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {enabled && (
        <div className="flex items-center gap-2 text-sm p-4 border border-gray-200 rounded-lg">
          <span className="text-gray-600">Recibir email a partir de nivel:</span>
          <select
            value={minLevel}
            onChange={(e) => { const v = e.target.value as Level; setMinLevel(v); void save({ enabled, minLevel: v }); }}
            disabled={saving}
            className="rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
          >
            <option value="info">Info (todas)</option>
            <option value="warning">Aviso</option>
            <option value="critical">Crítico</option>
          </select>
        </div>
      )}
    </div>
  );
}
